import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { Phone, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { pad } from '@/lib/format'
import { Logo } from '@/components/generals/logo'
import { ButtonLink } from '@/components/generals/button'
import { TransitionLink } from '@/components/generals/transition-link'
import { mainNav, siteConfig, QUOTE_PATH } from '@/constants/site'

interface MobileMenuProps {
    open: boolean
    onClose: () => void
}

const links = [{ label: "Home", href: '/' }, ...mainNav]

export const MobileMenu: FC<MobileMenuProps> = ({ open, onClose }): ReactNode => {
    const closeRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        if (!open) return

        const root = document.documentElement
        const previous = root.style.overflow

        root.style.overflow = 'hidden'
        closeRef.current?.focus()

        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }

        window.addEventListener('keydown', handleKey)

        return () => {
            root.style.overflow = previous
            window.removeEventListener('keydown', handleKey)
        }
    }, [open, onClose])

    return (
        <div
            role='dialog'
            aria-modal='true'
            aria-label="Menu"
            className={cn(
                'fixed inset-0 z-[70] transition-[visibility] duration-700 lg:hidden',
                open ? 'visible' : 'invisible'
            )}
        >
            <div
                aria-hidden='true'
                className={cn(
                    'absolute inset-0 bg-ink transition-[clip-path] duration-700 ease-door',
                    open ? '[clip-path:inset(0_0_0_0)]' : '[clip-path:inset(0_0_100%_0)]'
                )}
            >
                <div className='door-grooves absolute inset-0 opacity-40' />
            </div>

            <div className='relative flex h-full flex-col overflow-y-auto px-5 pb-8 sm:px-6'>
                <div className='flex h-[72px] shrink-0 items-center justify-between'>
                    <Logo tone='light' />

                    <button
                        ref={closeRef}
                        type='button'
                        onClick={onClose}
                        aria-label="Close menu"
                        className='flex size-11 items-center justify-center rounded-full border border-cream/20 text-cream transition-colors hover:bg-cream/10'
                    >
                        <X className='size-5' />
                    </button>
                </div>

                <nav aria-label="Mobile" className='mt-6'>
                    <ul>
                        {links.map((link, index) => (
                            <li key={link.href} className='overflow-hidden border-b border-cream/10'>
                                <TransitionLink
                                    href={link.href}
                                    onClick={onClose}
                                    className='flex items-end justify-between py-3.5'
                                >
                                    <span
                                        className={cn(
                                            'block font-heading text-[2.35rem] font-semibold leading-none tracking-[-0.045em] text-cream transition-transform duration-700 ease-expo',
                                            open ? 'translate-y-0' : 'translate-y-[115%]'
                                        )}
                                        style={{ transitionDelay: open ? `${160 + index * 50}ms` : '0ms' }}
                                    >
                                        {link.label}
                                    </span>

                                    <span className='pb-1 font-mono text-[11px] text-cream/35'>
                                        {pad(index + 1)}
                                    </span>
                                </TransitionLink>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div
                    className={cn(
                        'mt-auto space-y-3 pt-10 transition-[opacity,transform,translate,scale,rotate] duration-700 ease-expo',
                        open ? 'translate-y-0 opacity-100 delay-500' : 'translate-y-4 opacity-0'
                    )}
                >
                    <ButtonLink href={QUOTE_PATH} size='lg' onClick={onClose} className='w-full justify-between'>
                        Start a 60-second quote
                    </ButtonLink>

                    <a
                        href={siteConfig.phoneHref}
                        className='flex h-14 items-center justify-between rounded-full border border-cream/20 pl-6 pr-2 text-cream'
                    >
                        <span>Call <span className='font-mono'>{siteConfig.phone}</span></span>

                        <span className='flex size-10 items-center justify-center rounded-full bg-cream text-ink'>
                            <Phone className='size-4' />
                        </span>
                    </a>

                    <p className='pt-3 text-sm leading-relaxed text-cream/50'>
                        {siteConfig.address} · {siteConfig.hours} · {siteConfig.emergencyHours}
                    </p>
                </div>
            </div>
        </div>
    )
}
